import { SearchInput } from '@components/search/SearchInput'
import type { UseQueryResult } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp
} from 'lucide-react'
import {
  type Key,
  type ReactElement,
  type ReactNode,
  useEffect,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

import type { ColumnsType, PageRead } from './types'
import { PageReadKey } from './types'
import { usePageReadPayload } from './usePageReadPayload'

type AnyColumn = {
  title?: ReactNode
  dataIndex?: string | string[]
  key?: string
  render?: (value: unknown, record: unknown, index: number) => ReactNode
  sorter?: unknown
  align?: 'left' | 'right' | 'center'
  width?: number | string
}

type DefaultTableProps<T extends PageRead<object>> = {
  query: UseQueryResult<T, Error>
  columns: ColumnsType<T['results'][number]>
  id: string
  loading?: boolean
  rowClassName?:
    | string
    | ((record: T['results'][number], index: number) => string)
  rowSelection?: {
    getCheckboxProps?: (record: T['results'][number]) => { disabled?: boolean }
  }
  hasRowSelection?: boolean
  hasCreateButton?: boolean
  hasSearch?: boolean
  hasClick?: boolean
  onCreate?: () => void
  onRowCheck?: (selectedRowKeys: Key[], selectedRows: unknown[]) => void
  onRow?: (record: T['results'][number]) => { onClick?: () => void }
  actionBar?: ReactNode
}

const PAGE_SIZES = [10, 20, 50, 100]

function getValue(record: Record<string, unknown>, dataIndex?: string | string[]) {
  if (!dataIndex) return undefined
  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce<unknown>(
      (acc, key) => (acc as Record<string, unknown>)?.[key],
      record
    )
  }
  return record[dataIndex]
}

export function Table<T extends PageRead<object>>(
  props: DefaultTableProps<T>
): ReactElement {
  const {
    query,
    columns,
    id,
    loading = false,
    rowClassName,
    rowSelection,
    hasRowSelection = false,
    hasCreateButton = false,
    hasSearch = true,
    hasClick = true,
    actionBar,
    onCreate,
    onRowCheck,
    onRow
  } = props

  const { t } = useTranslation('frontend')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { size, page } = usePageReadPayload(searchParams)
  const [selected, setSelected] = useState<Set<Key>>(new Set())

  const cols = columns as unknown as AnyColumn[]
  const results = (query.data?.results ?? []) as Record<string, unknown>[]
  const total = query.data?.count ?? 0
  const pages = Math.max(1, Math.ceil(total / size))
  const isLoading = query.isFetching || loading

  const ordering = searchParams.get('ordering') || ''
  const sortField = ordering.replace(/^-/, '')
  const sortDesc = ordering.startsWith('-')

  const updateParams = (mutate: (p: URLSearchParams) => void) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      mutate(next)
      return next
    })

  const goToPage = (newPage: number) =>
    updateParams((p) => {
      p.set(PageReadKey.Offset, String((newPage - 1) * size))
      p.set(PageReadKey.Limit, String(size))
    })

  const changeSize = (newSize: number) =>
    updateParams((p) => {
      p.set(PageReadKey.Offset, '0')
      p.set(PageReadKey.Limit, String(newSize))
    })

  const toggleSort = (field: string) =>
    updateParams((p) => {
      p.set(PageReadKey.Offset, '0')
      if (sortField !== field) p.set('ordering', field)
      else if (!sortDesc) p.set('ordering', `-${field}`)
      else p.delete('ordering')
    })

  // The API pages on `count`/`limit` and never returns a `pages` field, so the
  // page number can outrun the result set (a narrowed search, a deleted row, a
  // hand-edited URL). Snap back to the first page instead of showing a blank one.
  useEffect(
    function resetPageIfOutOfRange() {
      if (total > 0 && page > pages) {
        updateParams((p) => p.set(PageReadKey.Offset, '0'))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [total, pages, page]
  )

  // Selection helpers
  const isDisabled = (record: Record<string, unknown>) =>
    rowSelection?.getCheckboxProps?.(record as T['results'][number])
      ?.disabled ?? false
  const selectableKeys = results
    .filter((r) => !isDisabled(r))
    .map((r) => r.id as Key)
  const allChecked =
    selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k))

  const emitSelection = (next: Set<Key>) => {
    setSelected(next)
    const rows = results.filter((r) => next.has(r.id as Key))
    onRowCheck?.(Array.from(next), rows)
  }

  const toggleRow = (key: Key) => {
    const next = new Set(selected)
    next.has(key) ? next.delete(key) : next.add(key)
    emitSelection(next)
  }

  const toggleAll = (checked: boolean) => {
    const next = new Set(selected)
    selectableKeys.forEach((k) => (checked ? next.add(k) : next.delete(k)))
    emitSelection(next)
  }

  const handleRowClick = (record: Record<string, unknown>) => {
    if (onRow) {
      onRow(record as T['results'][number]).onClick?.()
      return
    }
    if (hasClick) navigate(`/admin/${id}/${record.id}`)
  }

  const colCount = cols.length + (hasRowSelection ? 1 : 0)

  return (
    <div className="space-y-4">
      {(hasCreateButton || actionBar || hasSearch) && (
        <div className="flex flex-wrap items-center gap-3">
          {hasCreateButton && (
            <Button
              onClick={() =>
                onCreate ? onCreate() : navigate(`/admin/${id}/create`)
              }
            >
              {t(`frontend.table.create.${id}`)}
            </Button>
          )}
          {actionBar}
          {hasSearch && <SearchInput />}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-ink/[0.06] bg-white shadow-[0_10px_30px_rgba(4,33,37,0.05)]">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-ink/[0.08] bg-ink/[0.02] text-left">
                {hasRowSelection && (
                  <th className="w-12 px-4 py-3">
                    <Checkbox
                      checked={allChecked}
                      onCheckedChange={(c) => toggleAll(!!c)}
                      aria-label="select all"
                    />
                  </th>
                )}
                {cols.map((col, i) => {
                  const field =
                    typeof col.dataIndex === 'string' ? col.dataIndex : undefined
                  const sortable = !!col.sorter && !!field
                  const active = sortable && sortField === field
                  return (
                    <th
                      key={col.key ?? field ?? i}
                      className={cn(
                        'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground',
                        col.align === 'right' && 'text-right',
                        col.align === 'center' && 'text-center',
                        sortable && 'cursor-pointer select-none'
                      )}
                      onClick={sortable ? () => toggleSort(field!) : undefined}
                    >
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5',
                          col.align === 'right' && 'flex-row-reverse'
                        )}
                      >
                        {col.title}
                        {sortable &&
                          (active ? (
                            sortDesc ? (
                              <ChevronDown className="h-3.5 w-3.5 text-primary" />
                            ) : (
                              <ChevronUp className="h-3.5 w-3.5 text-primary" />
                            )
                          ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5 text-ink/30" />
                          ))}
                      </span>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, r) => (
                  <tr key={r} className="border-b border-ink/[0.05]">
                    {Array.from({ length: colCount }).map((__, c) => (
                      <td key={c} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-ink/[0.06]" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : results.length === 0 ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="px-4 py-12 text-center text-sm text-muted-foreground"
                  >
                    {t('frontend.table.empty', { defaultValue: '—' })}
                  </td>
                </tr>
              ) : (
                results.map((record, index) => {
                  const key = record.id as Key
                  const extra =
                    typeof rowClassName === 'function'
                      ? rowClassName(record as T['results'][number], index)
                      : rowClassName
                  return (
                    <tr
                      key={key}
                      onClick={() => handleRowClick(record)}
                      className={cn(
                        'border-b border-ink/[0.05] transition-colors last:border-0',
                        (hasClick || onRow) && 'cursor-pointer hover:bg-ink/[0.015]',
                        extra
                      )}
                    >
                      {hasRowSelection && (
                        <td
                          className="px-4 py-4"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selected.has(key)}
                            disabled={isDisabled(record)}
                            onCheckedChange={() => toggleRow(key)}
                            aria-label="select row"
                          />
                        </td>
                      )}
                      {cols.map((col, i) => {
                        const value = getValue(record, col.dataIndex)
                        return (
                          <td
                            key={col.key ?? i}
                            className={cn(
                              'px-4 py-4 align-middle text-sm text-ink',
                              col.align === 'right' && 'text-right',
                              col.align === 'center' && 'text-center'
                            )}
                          >
                            {col.render
                              ? col.render(value, record, index)
                              : (value as ReactNode)}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink/[0.06] px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{t('frontend.table.rowsPerPage', { defaultValue: 'Riadkov' })}</span>
              <select
                value={size}
                onChange={(e) => changeSize(Number(e.target.value))}
                className="rounded-lg border border-ink/15 bg-white px-2 py-1 text-sm text-ink outline-none focus:border-primary"
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="px-2 text-sm font-medium text-muted-foreground">
                {page} / {pages}
              </span>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => goToPage(page + 1)}
                className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
